import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { txt } from '../utils/bilingualText';
import { BACKEND_URL } from '../utils/backendUrl';


/**
 * NEXUS V6.3 Phase 5 - Spider Web V3 (Studio-Embedded Configuration)
 * Integrates with Knowledge Packs V3 for intelligent pack selection
 * 
 * Features:
 * - Pack selectors (not just weight sliders)
 * - AI-powered recommendations
 * - Real-time tone preview
 * - Compliance validation
 * - Context assembly preview
 */

const SpiderWebV3 = ({ studioType = 'content', objective = 'sales', embedded = false, onConfigSave }) => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const t = useLang();
  
  const [mode, setMode] = useState('quick'); // 'quick' or 'advanced'
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Selected packs
  const [selectedPacks, setSelectedPacks] = useState({
    product: null,
    direction: null,
    promotion: null,
    ambassador: null,
    trend: null
  });
  
  // Weights (for quick mode)
  const [weights, setWeights] = useState({
    product: 50,
    direction: 50,
    promotion: 50,
    ambassador: 50,
    trend: 50,
    health: 100, // Locked
    values: 100   // Locked
  });
  
  // Available packs (for dropdowns)
  const [availablePacks, setAvailablePacks] = useState({
    product: [],
    direction: [],
    promotion: [],
    ambassador: [],
    trend: []
  });
  
  // Recommendations
  const [recommendations, setRecommendations] = useState([]);
  
  // Previews
  const [tonePreview, setTonePreview] = useState(null);
  const [complianceRisk, setComplianceRisk] = useState(null);
  
  // UI state
  const [showRecommendations, setShowRecommendations] = useState(false);
  const [showTonePreview, setShowTonePreview] = useState(false);

  useEffect(() => {
    loadAvailablePacks();
    loadRecommendations();
  }, []);

  const loadAvailablePacks = async () => {
    try {
      setLoading(true);
      
      // Fetch published packs for each type
      const packTypes = ['product', 'direction', 'promotion', 'ambassador', 'trend'];
      const packData = {};
      
      for (const packType of packTypes) {
        const response = await fetch(
          `${BACKEND_URL}/api/v3/packs?pack_type=${packType}&status=published&studio=${studioType}&limit=50`,
          {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          }
        );
        
        if (response.ok) {
          const data = await response.json();
          packData[packType] = data.packs || [];
        } else {
          packData[packType] = [];
        }
      }
      
      setAvailablePacks(packData);
    } catch (err) {
      console.error('Failed to load packs:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRecommendations = async () => {
    try {
      const selectedIds = Object.values(selectedPacks).filter(Boolean);
      
      const response = await fetch(
        `${BACKEND_URL}/api/v3/recommendations?studio_type=${studioType}&objective=${objective}&limit=5`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ already_selected: selectedIds })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setRecommendations(data.recommendations || []);
      }
    } catch (err) {
      console.error('Failed to load recommendations:', err);
    }
  };

  const handlePackSelect = (packType, packId) => {
    setSelectedPacks(prev => ({
      ...prev,
      [packType]: packId
    }));
    
    // Reload recommendations when selection changes
    setTimeout(loadRecommendations, 500);
  };

  const handleWeightChange = (axis, value) => {
    setWeights(prev => ({
      ...prev,
      [axis]: parseInt(value, 10)
    }));
  };

  const previewTone = async () => {
    const selectedIds = Object.values(selectedPacks).filter(Boolean);
    
    if (selectedIds.length === 0) {
      alert('Please select at least one pack');
      return;
    }
    
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/v3/tone/project`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            pack_ids: selectedIds,
            weights: weights
          })
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setTonePreview(data.tone_projection);
        setShowTonePreview(true);
      }
    } catch (err) {
      console.error('Failed to preview tone:', err);
    }
  };

  const handleSave = async () => {
    const selectedIds = Object.values(selectedPacks).filter(Boolean);
    
    if (mode === 'advanced' && selectedIds.length === 0) {
      alert('Please select at least one pack');
      return;
    }
    
    try {
      setSaving(true);
      
      // Save configuration
      const config = {
        mode,
        selected_packs: selectedPacks,
        weights,
        studio_type: studioType,
        objective
      };
      
      // If embedded in studio, callback to parent
      if (embedded && onConfigSave) {
        onConfigSave(config);
      } else {
        // Standalone save to user preferences
        // TODO: Save to backend spider_web_configs collection
        alert('Configuration saved!');
      }
    } catch (err) {
      console.error('Failed to save:', err);
      alert('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const renderQuickMode = () => (
    <div className="space-y-4">
      {/* Weight Sliders */}
      {Object.entries(weights).map(([axis, value]) => {
        const isLocked = axis === 'health' || axis === 'values';
        
        return (
          <div key={axis} className="bg-white rounded-lg p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-bold text-gray-800 capitalize flex items-center gap-2">
                {axis === 'product' && '📦'}
                {axis === 'direction' && '🎯'}
                {axis === 'promotion' && '🎁'}
                {axis === 'ambassador' && '👤'}
                {axis === 'trend' && '📈'}
                {axis === 'health' && '🏥'}
                {axis === 'values' && '⭐'}
                {axis}
                {isLocked && <span className="text-xs text-red-500">🔒 Locked</span>}
              </label>
              <span className="text-lg font-black text-[#0e67a2]">{value}%</span>
            </div>
            
            <input
              type="range"
              min="0"
              max="100"
              value={value}
              onChange={(e) => handleWeightChange(axis, e.target.value)}
              disabled={isLocked}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: isLocked
                  ? '#E5E7EB'
                  : `linear-gradient(to right, #0e67a2 0%, #0e67a2 ${value}%, #E5E7EB ${value}%, #E5E7EB 100%)`
              }}
            />
          </div>
        );
      })}
    </div>
  );

  const renderAdvancedMode = () => (
    <div className="space-y-4">
      {/* Product Selector */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <label className="text-sm font-bold text-gray-800 mb-2 block">
          📦 Product Pack
        </label>
        <select
          value={selectedPacks.product || ''}
          onChange={(e) => handlePackSelect('product', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">-- Select Product --</option>
          {availablePacks.product.map(pack => (
            <option key={pack.pack_id} value={pack.pack_id}>
              {txt(pack.name)}
            </option>
          ))}
        </select>
        
        {selectedPacks.product && (
          <div className="mt-2">
            <label className="text-xs font-semibold text-gray-600">Weight:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={weights.product}
              onChange={(e) => handleWeightChange('product', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg mt-1"
            />
            <span className="text-xs text-gray-600">{weights.product}%</span>
          </div>
        )}
      </div>

      {/* Direction Selector */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <label className="text-sm font-bold text-gray-800 mb-2 block">
          🎯 Campaign Direction
        </label>
        <select
          value={selectedPacks.direction || ''}
          onChange={(e) => handlePackSelect('direction', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">-- Select Direction --</option>
          {availablePacks.direction.map(pack => (
            <option key={pack.pack_id} value={pack.pack_id}>
              {txt(pack.name)}
            </option>
          ))}
        </select>
        
        {selectedPacks.direction && (
          <div className="mt-2">
            <label className="text-xs font-semibold text-gray-600">Weight:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={weights.direction}
              onChange={(e) => handleWeightChange('direction', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg mt-1"
            />
            <span className="text-xs text-gray-600">{weights.direction}%</span>
          </div>
        )}
      </div>

      {/* Ambassador Selector */}
      <div className="bg-white rounded-lg p-4 shadow-sm">
        <label className="text-sm font-bold text-gray-800 mb-2 block">
          👤 Ambassador
        </label>
        <select
          value={selectedPacks.ambassador || ''}
          onChange={(e) => handlePackSelect('ambassador', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          <option value="">-- Select Ambassador --</option>
          {availablePacks.ambassador.map(pack => (
            <option key={pack.pack_id} value={pack.pack_id}>
              {txt(pack.name)}
            </option>
          ))}
        </select>
        
        {selectedPacks.ambassador && (
          <div className="mt-2">
            <label className="text-xs font-semibold text-gray-600">Weight:</label>
            <input
              type="range"
              min="0"
              max="100"
              value={weights.ambassador}
              onChange={(e) => handleWeightChange('ambassador', e.target.value)}
              className="w-full h-2 bg-gray-200 rounded-lg mt-1"
            />
            <span className="text-xs text-gray-600">{weights.ambassador}%</span>
          </div>
        )}
      </div>

      {/* Hard Constraints (Locked) */}
      <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
        <p className="text-xs font-bold text-gray-700 mb-2">
          🔒 Hard Constraints (Always Active)
        </p>
        <div className="flex items-center justify-between text-xs">
          <span>🏥 Health Context</span>
          <span className="font-bold text-green-600">100%</span>
        </div>
        <div className="flex items-center justify-between text-xs mt-1">
          <span>⭐ Company Values</span>
          <span className="font-bold text-green-600">100%</span>
        </div>
      </div>
    </div>
  );

  const renderRecommendations = () => {
    if (!showRecommendations || recommendations.length === 0) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[80vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
            <h3 className="text-lg font-black text-gray-900">
              💡 AI Recommendations
            </h3>
            <button
              onClick={() => setShowRecommendations(false)}
              className="text-gray-500 hover:text-gray-700 text-xl font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="p-4 space-y-3">
            {recommendations.map((rec, idx) => (
              <div
                key={`item-${idx}`}
                className="bg-blue-50 border border-blue-200 rounded-lg p-3"
              >
                <div className="flex items-start justify-between mb-1">
                  <span className="text-sm font-bold text-gray-900">
                    {txt(rec.pack.name)}
                  </span>
                  <span className="text-xs font-bold text-blue-600">
                    Score: {(rec.score * 100).toFixed(0)}%
                  </span>
                </div>
                <p className="text-xs text-gray-600 mb-2">
                  {rec.reason}
                </p>
                <button
                  onClick={() => {
                    handlePackSelect(rec.pack.pack_type, rec.pack.pack_id);
                    setShowRecommendations(false);
                  }}
                  className="w-full py-1.5 bg-blue-500 text-white text-xs font-bold rounded-lg hover:bg-blue-600"
                >
                  Select This Pack
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderTonePreview = () => {
    if (!showTonePreview || !tonePreview) return null;
    
    const { tone_profile, dominant_tone } = tonePreview;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-4 py-3 rounded-t-2xl flex items-center justify-between">
            <h3 className="text-lg font-black">
              🎨 Tone Preview
            </h3>
            <button
              onClick={() => setShowTonePreview(false)}
              className="text-white hover:text-gray-200 text-xl font-bold"
            >
              ×
            </button>
          </div>
          
          <div className="p-4">
            <div className="bg-purple-50 rounded-lg p-3 mb-4">
              <p className="text-xs font-bold text-purple-700 mb-1">Dominant Tone</p>
              <p className="text-2xl font-black text-purple-900 capitalize">
                {dominant_tone.type} ({(dominant_tone.score * 100).toFixed(0)}%)
              </p>
            </div>
            
            <p className="text-xs font-bold text-gray-700 mb-2">Tone Profile:</p>
            <div className="space-y-2">
              {Object.entries(tone_profile).map(([tone, score]) => (
                <div key={tone}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-semibold text-gray-700 capitalize">
                      {tone}
                    </span>
                    <span className="text-xs font-bold text-gray-900">
                      {(score * 100).toFixed(0)}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="bg-purple-500 h-2 rounded-full"
                      style={{ width: `${score * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="text-5xl mb-3">⏳</div>
          <p className="text-sm font-semibold text-gray-700">Loading packs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-white" style={{ height: embedded ? 'auto' : '100dvh' }}>
      {/* Header */}
      {!embedded && (
        <div className="bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white px-4 py-3 shadow-md">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center text-lg">
                🕸️
              </div>
              <div>
                <h1 className="text-lg font-black leading-tight">Spider Web V3</h1>
                <p className="text-xs font-medium opacity-90">AI-Powered Pack Configuration</p>
              </div>
            </div>
            <button
              onClick={() => navigate(-1)}
              className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-xs font-semibold transition"
            >
              Back
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="px-4 py-4 pb-24 max-w-2xl mx-auto">
        {/* Mode Toggle */}
        <div className="flex gap-2 mb-4">
          <button
            onClick={() => setMode('quick')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition ${
              mode === 'quick'
                ? 'bg-[#0e67a2] text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            ⚡ Quick Mode
          </button>
          <button
            onClick={() => setMode('advanced')}
            className={`flex-1 py-2.5 rounded-lg text-sm font-bold transition ${
              mode === 'advanced'
                ? 'bg-[#0e67a2] text-white'
                : 'bg-white text-gray-700 border border-gray-300'
            }`}
          >
            🎯 Advanced Mode
          </button>
        </div>

        {/* Mode Content */}
        {mode === 'quick' ? renderQuickMode() : renderAdvancedMode()}

        {/* Action Buttons */}
        <div className="mt-6 space-y-2">
          {mode === 'advanced' && (
            <>
              <button
                onClick={() => setShowRecommendations(true)}
                className="w-full py-3 bg-gradient-to-r from-green-500 to-green-600 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition"
              >
                💡 View AI Recommendations
              </button>
              
              <button
                onClick={previewTone}
                className="w-full py-3 bg-gradient-to-r from-purple-500 to-purple-600 text-white font-bold rounded-lg shadow-md hover:shadow-lg transition"
              >
                🎨 Preview Tone
              </button>
            </>
          )}
          
          <button
            onClick={handleSave}
            disabled={saving}
            className="w-full py-3 bg-gradient-to-r from-[#0e67a2] to-[#0a5a8a] text-white font-bold rounded-lg shadow-md hover:shadow-lg transition disabled:opacity-50"
          >
            {saving ? '💾 Saving...' : '💾 Save Configuration'}
          </button>
        </div>
      </div>

      {/* Modals */}
      {renderRecommendations()}
      {renderTonePreview()}
    </div>
  );
};

export default SpiderWebV3;
